/**
 * Shared rule-section schema for the Attendance Rules pages.
 *
 * DepartmentRules.tsx and GroupRules.tsx render the complete field set for
 * scoped overrides. GlobalRules.tsx uses GLOBAL_RULE_SECTIONS for editable
 * fields and shows rest_days as a read-only summary linked to Work Calendar,
 * the single writer for the organization default work week.
 *
 * Field keys ARE the backend's AttendanceRule column names so the
 * form state doubles as a valid AttendanceRuleUpdate payload without
 * a camelCase ↔ snake_case marshal layer.
 */

import type { AttendanceRuleUpdate } from '../../../api/attendance';

export type RuleKey = keyof AttendanceRuleUpdate;

export interface RuleField {
  /** Matches the backend AttendanceRuleUpdate field name. */
  key: RuleKey;
  /** i18n key suffix under attendance:ruleSections.fields.<camelKey> */
  i18nKey: string;
  type: 'minutes' | 'toggle' | 'days' | 'select';
  options?: { value: string | number; i18nKey: string }[];
}

export interface RuleSection {
  /** i18n key suffix under attendance:ruleSections.<sectionKey> */
  i18nKey: string;
  fields: RuleField[];
}

export const MINUTE_OPTIONS = [
  5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 240, 300, 360, 480,
];

/**
 * Weekday options for the `rest_days` picker.
 *
 * `value` is what goes to the API and into the database; `i18nKey` is what looks up the label. They
 * are separate on purpose, because they used to be the same lowercase string and that was the bug:
 * `app/attendance/calculation.py` compares against `WEEKDAY_ABBR = ["Mon", …]` with a case-sensitive
 * `in`, so every rest day chosen here was invisible to the engine and the worker was marked absent
 * on a day the admin had marked as rest. Measured on production 2026-08-11: one tenant's rule held
 * five lowercase days the engine could not read, and their report showed 40 absent days out of 69.
 *
 * The summary further down this module already normalised with `.toLowerCase()` — someone met the
 * same casing problem there and patched it locally instead of at the source, which left two
 * different notions of a weekday in one file.
 */
export const DAY_OPTIONS: { value: string; i18nKey: string }[] = [
  { value: 'Mon', i18nKey: 'mon' },
  { value: 'Tue', i18nKey: 'tue' },
  { value: 'Wed', i18nKey: 'wed' },
  { value: 'Thu', i18nKey: 'thu' },
  { value: 'Fri', i18nKey: 'fri' },
  { value: 'Sat', i18nKey: 'sat' },
  { value: 'Sun', i18nKey: 'sun' },
];

/** Case-insensitive membership, so a rule saved before the fix still displays correctly. */
export const isRestDaySelected = (selected: unknown, value: string): boolean =>
  Array.isArray(selected) &&
  selected.some((d) => typeof d === 'string' && d.trim().toLowerCase() === value.toLowerCase());

export const RULE_SECTIONS: RuleSection[] = [
  {
    i18nKey: 'clockInWindow',
    fields: [
      { key: 'rest_days', i18nKey: 'restDays', type: 'days' },
    ],
  },
  {
    i18nKey: 'lateEarlyLeave',
    fields: [
      { key: 'grace_period_min', i18nKey: 'gracePeriodMin', type: 'minutes' },
      { key: 'late_half_day_min', i18nKey: 'lateHalfDayMin', type: 'minutes' },
      { key: 'late_full_day_min', i18nKey: 'lateFullDayMin', type: 'minutes' },
      { key: 'early_leave_half_day_min', i18nKey: 'earlyLeaveHalfDayMin', type: 'minutes' },
      { key: 'early_leave_full_day_min', i18nKey: 'earlyLeaveFullDayMin', type: 'minutes' },
      { key: 'enable_half_day', i18nKey: 'enableHalfDay', type: 'toggle' },
    ],
  },
  {
    i18nKey: 'overtime',
    fields: [
      { key: 'ot_enabled', i18nKey: 'otEnabled', type: 'toggle' },
      { key: 'ot_require_approval', i18nKey: 'otRequireApproval', type: 'toggle' },
      { key: 'ot_minimum_min', i18nKey: 'otMinimumMin', type: 'minutes' },
      {
        key: 'ot_rounding_min', i18nKey: 'otRoundingMin', type: 'select',
        options: [
          { value: 1, i18nKey: 'noRounding' },
          { value: 15, i18nKey: 'minutes15' },
          { value: 30, i18nKey: 'minutes30' },
          { value: 60, i18nKey: 'hour1' },
        ],
      },
      {
        key: 'ot_daily_cap_min', i18nKey: 'otDailyCapMin', type: 'select',
        options: [
          { value: 0, i18nKey: 'noLimit' },
          { value: 60, i18nKey: 'hours1' },
          { value: 120, i18nKey: 'hours2' },
          { value: 180, i18nKey: 'hours3' },
          { value: 240, i18nKey: 'hours4' },
          { value: 360, i18nKey: 'hours6' },
        ],
      },
      {
        key: 'ot_weekly_limit_min', i18nKey: 'otWeeklyLimitMin', type: 'select',
        options: [
          { value: 0, i18nKey: 'noLimit' },
          { value: 600, i18nKey: 'hours10' },
          { value: 900, i18nKey: 'hours15' },
          { value: 1200, i18nKey: 'hours20' },
        ],
      },
    ],
  },
  {
    i18nKey: 'missedClockIn',
    fields: [
      { key: 'auto_flag_missed_in', i18nKey: 'autoFlagMissedIn', type: 'toggle' },
      { key: 'auto_flag_missed_out', i18nKey: 'autoFlagMissedOut', type: 'toggle' },
      { key: 'allow_retroactive', i18nKey: 'allowRetroactive', type: 'toggle' },
      {
        key: 'retroactive_days', i18nKey: 'retroactiveDays', type: 'select',
        options: [
          { value: 1, i18nKey: 'day1' },
          { value: 3, i18nKey: 'days3' },
          { value: 7, i18nKey: 'days7' },
          { value: 14, i18nKey: 'days14' },
        ],
      },
      {
        key: 'work_day_start_hour', i18nKey: 'workDayStartHour', type: 'select',
        options: [
          { value: 0, i18nKey: 'midnight' },
          { value: 3, i18nKey: 'am3' },
          { value: 5, i18nKey: 'am5' },
          { value: 6, i18nKey: 'am6' },
        ],
      },
      {
        key: 'night_shift_mode', i18nKey: 'nightShiftMode', type: 'select',
        options: [
          { value: 'start', i18nKey: 'dayStarted' },
          { value: 'end', i18nKey: 'dayEnded' },
          { value: 'major', i18nKey: 'dayMajor' },
        ],
      },
    ],
  },
  {
    i18nKey: 'mobileClockIn',
    fields: [
      { key: 'require_photo', i18nKey: 'requirePhoto', type: 'toggle' },
      { key: 'require_face', i18nKey: 'requireFace', type: 'toggle' },
      { key: 'require_liveness', i18nKey: 'requireLiveness', type: 'toggle' },
      {
        key: 'liveness_threshold', i18nKey: 'livenessThreshold', type: 'select',
        options: [
          { value: 70, i18nKey: 'liveThr70' },
          { value: 80, i18nKey: 'liveThr80' },
          { value: 90, i18nKey: 'liveThr90' },
        ],
      },
      {
        key: 'liveness_sample_rate', i18nKey: 'livenessSampleRate', type: 'select',
        options: [
          { value: 0, i18nKey: 'sampleOff' },
          { value: 5, i18nKey: 'sample5' },
          { value: 10, i18nKey: 'sample10' },
          { value: 20, i18nKey: 'sample20' },
          { value: 50, i18nKey: 'sample50' },
        ],
      },
      {
        key: 'face_match_threshold', i18nKey: 'faceMatchThreshold', type: 'select',
        options: [
          { value: 0.8, i18nKey: 'faceThr80' },
          { value: 0.85, i18nKey: 'faceThr85' },
          { value: 0.9, i18nKey: 'faceThr90' },
        ],
      },
      {
        key: 'face_max_retries', i18nKey: 'faceMaxRetries', type: 'select',
        options: [
          { value: 1, i18nKey: 'retries1' },
          { value: 2, i18nKey: 'retries2' },
          { value: 3, i18nKey: 'retries3' },
          { value: 5, i18nKey: 'retries5' },
        ],
      },
      {
        key: 'face_fail_policy', i18nKey: 'faceFailPolicy', type: 'select',
        options: [
          { value: 'block', i18nKey: 'failBlock' },
          { value: 'pending_review', i18nKey: 'failPending' },
          { value: 'allow', i18nKey: 'failAllow' },
        ],
      },
      { key: 'block_mock_location', i18nKey: 'blockMockLocation', type: 'toggle' },
      { key: 'multi_device', i18nKey: 'multiDevice', type: 'toggle' },
      { key: 'gps_required', i18nKey: 'gpsRequired', type: 'toggle' },
      { key: 'enforce_geofence', i18nKey: 'enforceGeofence', type: 'toggle' },
    ],
  },
];

export const FULL_WIDTH_SECTION_KEY = 'mobileClockIn';

/**
 * Wide cards are intentional information groups, not an equal-height grid row.
 * Rest days is compact, while missed-punch and mobile-clock-in rules contain
 * multi-column detail. Giving each a full row prevents short cards from being
 * stretched to the height of an unrelated long card on wide admin screens.
 */
const WIDE_RULE_SECTION_KEYS = new Set([
  'clockInWindow',
  'missedClockIn',
  FULL_WIDTH_SECTION_KEY,
]);

export const isWideRuleSection = (sectionKey: string): boolean =>
  WIDE_RULE_SECTION_KEYS.has(sectionKey);

/**
 * Organization default work week is managed exclusively in Work Calendar.
 * Department and group overrides continue to use RULE_SECTIONS, including
 * rest_days, because their scoped schedules intentionally override the default.
 */
/**
 * The global rule page's editable sections. Identical to `RULE_SECTIONS` since 2026-08-12,
 * when the organization's default work week moved BACK here.
 *
 * It used to exclude `clockInWindow` — a section whose only field is `rest_days` — because
 * Work Calendar was designated "the single writer for the organization default work week"
 * and this page showed a read-only summary linking there. That page has become a read
 * model (it wrote `updateGlobal` while having no notion of department or group rules, so
 * its dots displayed a work week that could govern nobody), which left the setting with no
 * editor at all and the two pages pointing at each other.
 *
 * Kept as a named export rather than collapsed into `RULE_SECTIONS`: the three rule pages
 * read different section lists in principle, and a future scope-specific exclusion belongs
 * here rather than at a call site.
 */
export const GLOBAL_RULE_SECTIONS: RuleSection[] = RULE_SECTIONS;

/**
 * Build a form-state object from a fetched AttendanceRule row by
 * projecting out only the editable columns (drops id / org_id /
 * department_id / created_at). Returned shape is also a valid
 * AttendanceRuleUpdate payload.
 */
export function ruleToFormValues(
  rule: Record<string, unknown>,
  sections: RuleSection[] = RULE_SECTIONS,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const section of sections) {
    for (const field of section.fields) {
      out[field.key] = rule[field.key];
    }
  }
  return out;
}

/**
 * True when every day of the week is marked as rest — which switches attendance expectations OFF
 * entirely: nobody is ever expected to work, so nobody is ever late or absent.
 *
 * This guard exists BECAUSE of the casing fix. Before it, the engine compared case-sensitively and
 * silently ignored the lowercase days this picker wrote, so ticking all seven was harmless — one
 * production tenant had done exactly that and still had their weekdays scored. Now that the
 * configuration is honoured, the same click turns a working week into a permanent holiday. A change
 * that makes a previously inert setting consequential has to say so.
 */
export const isEveryDayRest = (selected: unknown): boolean =>
  Array.isArray(selected) &&
  DAY_OPTIONS.every((d) => isRestDaySelected(selected, d.value));

/**
 * Rule fields whose value changes the OUTPUT of the attendance calculation for days that
 * have already been scored. Changing one of these leaves every existing daily summary
 * disagreeing with the configuration until a recalculation is run — and the recalc cron
 * only revisits each org's local yesterday + today, so a month closed last week stays
 * wrong indefinitely.
 *
 * That is what the save confirmation names. The list is deliberately NOT "every field":
 * the capture-time gates (`require_face`, `enforce_geofence`, `block_mock_location`,
 * `multi_device`, `gps_required`, the photo/liveness settings) decide whether a punch can
 * be MADE, and re-scoring yesterday cannot retroactively change a punch that already
 * exists. A warning that fires on every save is a warning that gets clicked through, so
 * it fires only when it is true.
 *
 * The set lives here, beside the field definitions, because all three rule pages
 * (Global / Department / Group) must agree on it — the day this file was last touched was
 * spent fixing one setting that had two notions of itself in two places.
 */
export const SCORING_RULE_KEYS: readonly string[] = [
  'rest_days',
  'grace_period_min',
  'late_half_day_min',
  'late_full_day_min',
  'early_leave_half_day_min',
  'early_leave_full_day_min',
  'enable_half_day',
  'ot_enabled',
  'ot_minimum_min',
  'ot_rounding_min',
  'ot_daily_cap_min',
  'ot_weekly_limit_min',
  'auto_flag_missed_in',
  'auto_flag_missed_out',
  'work_day_start_hour',
  'night_shift_mode',
];

/** Which scoring fields differ between the loaded rule and the edited form values. */
export function changedScoringKeys(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown>,
): string[] {
  if (!before) return [];
  return SCORING_RULE_KEYS.filter((key) => {
    const a = before[key];
    const b = after[key];
    // rest_days is a list; compare order-insensitively and case-insensitively, because the
    // engine reads it that way and a reorder is not a change of meaning.
    if (Array.isArray(a) || Array.isArray(b)) {
      const norm = (v: unknown) => (Array.isArray(v) ? v.map((x) => String(x).trim().toLowerCase()).sort() : []);
      return JSON.stringify(norm(a)) !== JSON.stringify(norm(b));
    }
    // The form holds numbers as strings; '5' and 5 are the same rule.
    const scalar = (v: unknown) => (v === null || v === undefined || v === '' ? '' : String(v));
    return scalar(a) !== scalar(b);
  });
}

/**
 * The i18n key of a rule field, so a change can be NAMED with the label the form already
 * shows it under (`attendance:ruleSections.fields.<i18nKey>.label`, 35 entries already
 * present in all nine locales). Reused rather than duplicated: a second set of labels for
 * the same fields is the drift this file has already paid for once.
 */
export function ruleFieldI18nKey(key: string): string | null {
  for (const section of RULE_SECTIONS) {
    const field = section.fields.find((f) => f.key === key);
    if (field) return field.i18nKey;
  }
  return null;
}
